import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PartTercAttribute, PartTercAttributeSearchParams } from '../../services/partTercAttributesApi';

export interface PartTercAttributesState {
  items: PartTercAttribute[];
  selectedItem: PartTercAttribute | null;
  loading: boolean;
  error: string | null;
  formData: { PartSecAttributeId: number | null; Name: string; Description: string; Active: boolean };
  validationErrors: string[];
  searchParams: PartTercAttributeSearchParams;
  pagination: { page: number; pageSize: number; total: number };
  statistics: { total: number; active: number; inactive: number };
  selectedIds: number[];
  bulkOperationLoading: boolean;
  successMessage: string;
}

const initialState: PartTercAttributesState = {
  items: [], selectedItem: null, loading: false, error: null,
  formData: { PartSecAttributeId: null, Name: '', Description: '', Active: true },
  validationErrors: [],
  searchParams: { page: 1, pageSize: 10, search: '', active: undefined, sortBy: 'Name', sortOrder: 'asc' },
  pagination: { page: 1, pageSize: 10, total: 0 },
  statistics: { total: 0, active: 0, inactive: 0 },
  selectedIds: [], bulkOperationLoading: false, successMessage: '',
};

const partTercAttributesSlice = createSlice({
  name: 'partTercAttributes', initialState,
  reducers: {
    setLoading: (s, a: PayloadAction<boolean>) => { s.loading = a.payload; },
    setError: (s, a: PayloadAction<string | null>) => { s.error = a.payload; },
    setItems: (s, a: PayloadAction<PartTercAttribute[]>) => { s.items = a.payload; },
    setSelectedItem: (s, a: PayloadAction<PartTercAttribute | null>) => { s.selectedItem = a.payload; },
    addItem: (s, a: PayloadAction<PartTercAttribute>) => { s.items.unshift(a.payload); },
    updateItem: (s, a: PayloadAction<PartTercAttribute>) => {
      const i = s.items.findIndex(x => x.Id === a.payload.Id);
      if (i !== -1) s.items[i] = a.payload;
      if (s.selectedItem?.Id === a.payload.Id) s.selectedItem = a.payload;
    },
    removeItem: (s, a: PayloadAction<number>) => {
      s.items = s.items.filter(x => x.Id !== a.payload);
      if (s.selectedItem?.Id === a.payload) s.selectedItem = null;
    },
    setFormData: (s, a: PayloadAction<Partial<PartTercAttributesState['formData']>>) => { s.formData = { ...s.formData, ...a.payload }; },
    resetFormData: (s) => { s.formData = initialState.formData; },
    setValidationErrors: (s, a: PayloadAction<string[]>) => { s.validationErrors = a.payload; },
    clearValidationErrors: (s) => { s.validationErrors = []; },
    setSearchParams: (s, a: PayloadAction<Partial<PartTercAttributeSearchParams>>) => { s.searchParams = { ...s.searchParams, ...a.payload } as any; },
    resetSearchParams: (s) => { s.searchParams = initialState.searchParams; },
    setPagination: (s, a: PayloadAction<Partial<PartTercAttributesState['pagination']>>) => { s.pagination = { ...s.pagination, ...a.payload } as any; },
    setStatistics: (s, a: PayloadAction<PartTercAttributesState['statistics']>) => { s.statistics = a.payload; },
    setSelectedIds: (s, a: PayloadAction<number[]>) => { s.selectedIds = a.payload; },
    addSelectedId: (s, a: PayloadAction<number>) => { if (!s.selectedIds.includes(a.payload)) s.selectedIds.push(a.payload); },
    removeSelectedId: (s, a: PayloadAction<number>) => { s.selectedIds = s.selectedIds.filter(id => id !== a.payload); },
    clearSelectedIds: (s) => { s.selectedIds = []; },
    setBulkOperationLoading: (s, a: PayloadAction<boolean>) => { s.bulkOperationLoading = a.payload; },
    setSuccessMessage: (s, a: PayloadAction<string>) => { s.successMessage = a.payload; },
    clearSuccessMessage: (s) => { s.successMessage = ''; },
    resetPartTercAttributesState: () => initialState,
  }
});

export const {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setFormData, resetFormData, setValidationErrors, clearValidationErrors,
  setSearchParams, resetSearchParams, setPagination, setStatistics,
  setSelectedIds, addSelectedId, removeSelectedId, clearSelectedIds, setBulkOperationLoading,
  setSuccessMessage, clearSuccessMessage, resetPartTercAttributesState,
} = partTercAttributesSlice.actions;

export const FETCH_PART_TERC_ATTRIBUTES = 'partTercAttributes/fetch';
export const FETCH_PART_TERC_ATTRIBUTE_BY_ID = 'partTercAttributes/fetchById';
export const CREATE_PART_TERC_ATTRIBUTE = 'partTercAttributes/create';
export const UPDATE_PART_TERC_ATTRIBUTE = 'partTercAttributes/update';
export const DELETE_PART_TERC_ATTRIBUTE = 'partTercAttributes/delete';
export const SEARCH_PART_TERC_ATTRIBUTES = 'partTercAttributes/search';
export const FETCH_PART_TERC_ATTRIBUTES_STATS = 'partTercAttributes/fetchStats';
export const BULK_PART_TERC_ATTRIBUTES_ACTIVATE = 'partTercAttributes/bulkActivate';
export const BULK_PART_TERC_ATTRIBUTES_DEACTIVATE = 'partTercAttributes/bulkDeactivate';
export const BULK_PART_TERC_ATTRIBUTES_DELETE = 'partTercAttributes/bulkDelete';
export const EXPORT_PART_TERC_ATTRIBUTES_CSV = 'partTercAttributes/exportCsv';

export const fetchPartTercAttributes = (p?: PartTercAttributeSearchParams) => ({ type: FETCH_PART_TERC_ATTRIBUTES, payload: p });
export const fetchPartTercAttributeById = (id: number) => ({ type: FETCH_PART_TERC_ATTRIBUTE_BY_ID, payload: id });
export const createPartTercAttribute = (item: Omit<PartTercAttribute, 'Id'>) => ({ type: CREATE_PART_TERC_ATTRIBUTE, payload: item });
export const updatePartTercAttribute = (id: number, item: Partial<PartTercAttribute>) => ({ type: UPDATE_PART_TERC_ATTRIBUTE, payload: { id, item } });
export const deletePartTercAttribute = (id: number) => ({ type: DELETE_PART_TERC_ATTRIBUTE, payload: id });
export const searchPartTercAttributes = (p: PartTercAttributeSearchParams) => ({ type: SEARCH_PART_TERC_ATTRIBUTES, payload: p });
export const fetchPartTercAttributesStats = () => ({ type: FETCH_PART_TERC_ATTRIBUTES_STATS });
export const bulkPartTercAttributesActivate = (ids: number[]) => ({ type: BULK_PART_TERC_ATTRIBUTES_ACTIVATE, payload: ids });
export const bulkPartTercAttributesDeactivate = (ids: number[]) => ({ type: BULK_PART_TERC_ATTRIBUTES_DEACTIVATE, payload: ids });
export const bulkPartTercAttributesDelete = (ids: number[]) => ({ type: BULK_PART_TERC_ATTRIBUTES_DELETE, payload: ids });
export const exportPartTercAttributesCsv = (p?: PartTercAttributeSearchParams) => ({ type: EXPORT_PART_TERC_ATTRIBUTES_CSV, payload: p });

export default partTercAttributesSlice.reducer; 